import { Vector2, Vector3 } from '@technology/web3d-plugin-threejs';
import proj4 from 'proj4';
import { ProjectionDefinition } from 'proj4/dist/lib/defs.js';
import { TglTransformFunction } from '../tgl/tgl.js';
import { MapProjection } from '../types/map-projection.js';
import {
  QuantizedMeshOptions,
  TextureFetcher,
  TileFetcher,
} from '../types/quantized-mesh-options.js';
import { TileOptions } from '../types/tile-options.js';
import { DisplayMode } from '../types/display-mode.js';

export type QuantizedMeshSceneProjection = Omit<MapProjection, 'bounds'>;

export class QuantizedMeshSettings {
  public mapProj: ProjectionDefinition;
  public sceneProj!: ProjectionDefinition;

  public get maxZoomLevel(): number {
    return this.options.tileOptions.maxZoomLevel;
  }

  private refinementZoomCap: number | null = null;

  /**
   * How deep on-screen tiles may refine. Can be lowered at runtime (e.g. when
   * no layer has detail beyond a level) without affecting
   * {@link maxZoomLevel}, which bounds offscreen work such as profiles.
   */
  public get refinementMaxZoomLevel(): number {
    return Math.min(
      this.refinementZoomCap ?? this.maxZoomLevel,
      this.maxZoomLevel
    );
  }

  public set refinementMaxZoomLevel(level: number | null) {
    this.refinementZoomCap = level;
  }

  public get earlyRefinement(): boolean {
    return this._options.meshSettings.earlyRefinement === true;
  }

  public get imageSize(): number {
    return this.options.tileOptions.imageSize;
  }

  public get textureFetchers(): Array<TextureFetcher> {
    return this._options.textureFetchers?.() ?? [];
  }

  /** A comma separated list of texture fetcher ids representing the current textures applied to the mesh.
   *  This is used to determine whether a tile has stale textures and needs to be re-fetched.
   */
  public textureId = '';
  private textureGeneration = 0;
  /**
   * This method updates the textureId variable.
   * Ideally this would be set when the texture fetchers are set, but since it's an accessor this isn't feasible.
   * So instead this function is used to set the current textureId where required.
   */
  public updateTextureId(): void {
    this.textureGeneration++;
    this.textureId = `${this.textureFetchers.map((t) => t.id).join(',')}:${this.textureGeneration}`;
  }

  public get debug(): boolean {
    return this._options.debug;
  }

  public get tileFetcher(): TileFetcher {
    return this._options.tileFetcher;
  }

  private _options!: QuantizedMeshOptions;

  public get mapProjection(): MapProjection {
    return this._options.projection;
  }

  public get displayMode(): DisplayMode {
    return this._options.meshSettings.displayMode;
  }

  public set displayMode(mode: DisplayMode) {
    this._options.meshSettings.displayMode = mode;
  }

  constructor(
    options: QuantizedMeshOptions,
    public sceneProjection: QuantizedMeshSceneProjection,
    private readonly transformWithTgl: TglTransformFunction | null = null
  ) {
    this.options = options;
    this.mapProj = proj4.Proj(options.projection.proj4def!);

    if (sceneProjection.proj4def) {
      this.sceneProj = proj4.Proj(sceneProjection.proj4def);
    }
  }

  public set options(options: QuantizedMeshOptions) {
    this._options = options;
  }

  public get options(): QuantizedMeshOptions {
    return this._options;
  }

  public get tileOptions(): TileOptions {
    return this._options.tileOptions;
  }

  /**
   * Transforms map coordinate to scene coordinate.
   *
   * Note: Assumes that the given map coordinate is valid in the map coordinate system,
   * and that it can be projected to a valid point in scene coordinate system.
   */
  public mapToScene<T extends Vector2 | Vector3>(
    mapCoordinates: T,
    result: T
  ): T {
    return this.projectPoint(
      mapCoordinates,
      this.mapProj,
      this.sceneProj,
      result
    );
  }

  public projectPoint<T extends Vector2 | Vector3>(
    p: T,
    fromProjection: ProjectionDefinition,
    toProjection: ProjectionDefinition,
    result: T
  ): T {
    if (this.transformWithTgl !== null) {
      const tglResult = this.transformWithTgl(p.x, p.y, false);
      if (tglResult.errorCode === 0) {
        result.x = tglResult.x;
        result.y = tglResult.y;
      }
    } else {
      const res = proj4.transform(fromProjection, toProjection, p, false);
      result.x = res!.x;
      result.y = res!.y;
    }
    return result;
  }
}
